import { useState } from 'react';
import * as XLSX from 'xlsx';
import { theme } from '../../theme.js';
import ChartCard from './ChartCard.jsx';

const columns = [
  { key: 'bib_number', label: 'Bib Number' },
  { key: 'race_name', label: 'Race' },
  { key: 'participant_name', label: 'Name' },
  { key: 'participant_surname', label: 'Surname' },
  { key: 'email', label: 'Email' },
  { key: 'payment_amount', label: 'Amount' },
  { key: 'payment_status', label: 'Payment Status' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
    case 'completed':
      return { bg: '#D1FAE5', text: '#065F46' };
    case 'pending':
      return { bg: '#FEF3C7', text: '#92400E' };
    case 'failed':
    case 'cancelled':
      return { bg: '#FEE2E2', text: '#991B1B' };
    default:
      return { bg: '#E5E7EB', text: '#374151' };
  }
};

export default function ChartTable({ data = [] }) {
  const [sortConfig, setSortConfig] = useState({ key: 'bib_number', direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  // Ensure data is an array
  const tableData = Array.isArray(data) ? data : [];

  const sortedData = [...tableData].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleExportXLSX = () => {
    const exportData = tableData.map(row => ({
      'Bib Number': row.bib_number,
      'Race': row.race_name,
      'Name': row.participant_name,
      'Surname': row.participant_surname,
      'Email': row.email,
      'Amount': row.payment_amount,
      'Payment Status': row.payment_status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    const columnWidths = [
      { wch: 12 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 12 },
      { wch: 15 },
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bibs');
    
    XLSX.writeFile(workbook, 'bibs.xlsx');
  };

  return (
    <ChartCard>
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>Bibs & Participants</h3>
          <button style={styles.exportButton} onClick={handleExportXLSX}>
            📥 Export XLSX
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={styles.headerCell}
                    onClick={() => handleSort(col.key)}
                  >
                    <div style={styles.headerContent}>
                      {col.label}
                      {sortConfig.key === col.key && (
                        <span style={styles.sortIcon}>
                          {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.length > 0 ? (
                sortedData.map((row, idx) => (
                  <tr key={row.bib_id} style={{
                    ...styles.bodyRow,
                    backgroundColor: idx % 2 === 0 ? theme.colors.white : theme.colors.bgLight,
                  }}>
                    <td style={styles.cell}>{row.bib_number}</td>
                    <td style={styles.cell}>{row.race_name}</td>
                    <td style={styles.cell}>{row.participant_name}</td>
                    <td style={styles.cell}>{row.participant_surname}</td>
                    <td style={styles.cell}>{row.email}</td>
                    <td style={styles.cell}>${row.payment_amount?.toFixed(2) || '0.00'}</td>
                    <td style={styles.cell}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: getStatusColor(row.payment_status).bg,
                        color: getStatusColor(row.payment_status).text,
                      }}>
                        {row.payment_status || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} style={{ ...styles.cell, textAlign: 'center', padding: '40px' }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>Showing {sortedData.length} of {tableData.length} entries</p>
        </div>
      </div>
    </ChartCard>
  );
}

const styles = {
  container: {
    width: '100%',
    maxWidth: '900px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
    margin: 0,
  },
  exportButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    border: `1px solid ${theme.colors.light}`,
    backgroundColor: theme.colors.white,
    color: theme.colors.light,
    borderRadius: theme.radius.sm,
    cursor: 'pointer',
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.bold,
    transition: 'all 0.2s',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: theme.colors.bgLight,
    borderBottom: `2px solid ${theme.colors.border}`,
  },
  headerCell: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    textAlign: 'left',
    fontWeight: theme.fonts.weight.bold,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.2s',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  sortIcon: {
    fontSize: '0.75rem',
    color: theme.colors.light,
  },
  bodyRow: {
    borderBottom: `1px solid ${theme.colors.border}`,
    transition: 'background-color 0.2s',
  },
  cell: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
  },
  badge: {
    display: 'inline-block',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    fontSize: theme.fonts.size.xs,
    fontWeight: theme.fonts.weight.bold,
  },
  footer: {
    paddingTop: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.border}`,
  },
  footerText: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    margin: 0,
  },
};
